import React, { useState, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material'
import { Chip, MenuItem, Select, Tooltip } from '@mui/material'
import {
  AutoCompleteInput,
  Button,
  Col,
  CurrencyInput,
  Form,
  Grid,
  Input,
  Picker,
  Row,
  SelectForm,
  Span,
  AutoCompleteSearchTerm
} from 'components'
import { useLoading, useModal, useFile } from 'hooks'
import ApplyMasker from 'hooks/useMasker'
import moment from 'moment'
import { useForm, useFieldArray, useWatch } from 'react-hook-form'
import { If } from 'react-if'
import {
  ConventionServices,
  IAnexados,
  IRegistroHomologDetails
} from 'services/NGT/Convention'
import { SyndicateServices, IGetFiltroResponse } from 'services/NGT/Syndicate'
import { COLORS, SPACING, FORMAT } from 'utils'
import * as yup from 'yup'

import { ConsultDocsTable } from '../ConsultDocsTable/ConsultDocsTable'
import { MagnifyingGlassIcon } from 'components/Icons'
import { ResponseGeneric } from 'utils/types'

interface FormProps {
  setSelectedTab: any
}

const schema = yup
  .object({
    cdHomologacao: yup
      .string()
      .min(2, 'Este campo deve ter no mínimo 2 caracteres!')
      .max(20, 'Este campo deve ter no máximo 20 caracteres!')
      .required('Informe o Registro de Homologação'),
    dtVigenciaFim: yup
      .string()
      .nullable()
      .required('Informe o fim da vigência'),
    dtVigenciaInicio: yup
      .string()
      .nullable()
      .required('Informe o início da vigência'),
    flBancoHoras: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    flHorasExtras: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    flTurno: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    flValeAlimentacao: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    flValeRefeicao: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    nuAnoMesCompetencia: yup.array(),
    txObservacaoGeral: yup.string().max(225, 'Máximo de caracteres 225.'),
    txObservacaoBancoHoras: yup.string().max(225, 'Máximo de caracteres 225.'),
    txObservacaoHorasExtras: yup.string().max(225, 'Máximo de caracteres 225.'),
    txObservacaoTurno: yup.string().max(225, 'Máximo de caracteres 225.'),
    txTipo: yup
      .string()
      .min(2, 'Informe o Tipo')
      .nullable()
      .required('Informe o Tipo')
  })
  .required()

const DATE_NOW = new Date()

export const ConventionRegistrationForm: React.FC<FormProps> = ({
  setSelectedTab
}) => {
  const { setLoading, setMsgErro } = useLoading()
  const { showModal } = useModal()
  const { viewFile } = useFile()
  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    register
  } = useForm({
    defaultValues: {
      cdHomologacao: '',
      contratoId: 0,
      dtVigenciaFim: null,
      dtVigenciaInicio: null,
      flBancoHoras: '',
      flHorasExtras: '',
      flTurno: '',
      flValeAlimentacao: '',
      flValeRefeicao: '',
      fornecedorId: 0,
      nuAnoMesCompetencia: [],
      peInsalubridade: 0,
      pePericulosidade: 0,
      pePisoSalarial: 0,
      sindicatos: [{ cdCnpj: '', idSindicato: 0, nmSindicato: '' }],
      txObservacaoBancoHoras: '',
      txObservacaoGeral: '',
      txObservacaoHorasExtras: '',
      txObservacaoTurno: '',
      txTipo: '',
      vlReajuste: 0
    },
    resolver: yupResolver(schema)
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sindicatos'
  })

  const watchSindicatos = useWatch({
    control,
    name: 'sindicatos'
  })

  const dtVigenciaInicio = watch('dtVigenciaInicio')
  const dtVigenciaFim = watch('dtVigenciaFim')

  const [contratos, setContratos] = useState({} as any)
  const [cdContrato, setContrato] = useState<string>('')
  const [nmFornecedor, setNmFornecedor] = useState<string>('')
  const [cnpjFornecedor, setCnpjFornecedor] = useState<string>('')
  const [selectedContrato, setSelectedContrato] = useState<string>('')
  const [competenciasDisponiveis, setCompetenciasDisponiveis] = useState<
    string[]
  >([])
  const [contratoId, setContratoId] = useState<number>(0)
  const [fornecedorId, setfornecedorId] = useState<number>(0)
  const [attachDocsList, setAttachDocsList] = useState<IAnexados[]>([])
  const [sindicatos, setSindicatos] = useState<IGetFiltroResponse[]>([])
  const [competencias, setCompetencias] = useState<string[]>([])

  const areDatesValid = () => {
    return moment(dtVigenciaInicio).isValid() && moment(dtVigenciaFim).isValid()
  }

  const fillWithCdHomologacaoData = (values: IRegistroHomologDetails) => {
    setValue('flBancoHoras', values.flBancoHoras.toString())
    setValue('flHorasExtras', values.flHorasExtras.toString())
    setValue('flTurno', values.flTurno.toString())
    setValue('flValeAlimentacao', values.flValeAlimentacao.toString())
    setValue('flValeRefeicao', values.flValeRefeicao.toString())
    setValue('peInsalubridade', values.peInsalubridade)
    setValue('pePericulosidade', values.pePericulosidade)
    setValue('pePisoSalarial', values.pePisoSalarial)
    setValue('txObservacaoBancoHoras', values.txObservacaoBancoHoras)
    setValue('txObservacaoGeral', values.txObservacaoGeral)
    setValue('txObservacaoHorasExtras', values.txObservacaoHorasExtras)
    setValue('txObservacaoTurno', values.txObservacaoTurno)
    setValue('vlReajuste', values.vlReajuste)
    setValue('dtVigenciaInicio', moment(values.dtVigenciaInicio))
    setValue('dtVigenciaFim', moment(values.dtVigenciaFim))
  }

  const handleSearchCdHomologacao = () => {
    const cdHomologacao = control._formValues['cdHomologacao']
    setLoading('loading')
    ConventionServices()
      .getByRegistroHomologacao(cdHomologacao)
      .then((response) => {
        if (fields.length > 1) {
          for (let i = 1; i < fields.length; i++) {
            remove(i)
          }
        }
        const ac = response.data
        if (response.status == 204) {
          showModal({
            hideButtonCancel: true,
            message: 'Registro Homologação não encontrado.',
            txtButtonConfirm: 'Fechar',
            type: 'alert'
          })
          setLoading('idle')
          return
        }
        fillWithCdHomologacaoData(ac)
        ac.sindicatos.forEach((s, index) => {
          if (index > 0) append({ cdCnpj: '', idSindicato: 0, nmSindicato: '' })
          handleSelectSindicato({
            formPosition: index,
            cdCnpj: ApplyMasker(s.cdCnpj, ['##.###.###/####-##']),
            idSindicato: s.idSindicato,
            nmSindicato: s.nmSindicato
          })
        })
        setLoading('idle')
      })
  }

  const handleSearchContrato = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading('loading')
    ConventionServices()
      .getContractByCd(cdContrato)
      .then((response) => {
        const contract = response.data
        setContratos(contract)

        setNmFornecedor(contract.nmFornecedor)
        setCnpjFornecedor(contract.cnpjFornecedor)
        setfornecedorId(contract.idFornecedor)
        setContratoId(contract.idContrato)
        setCompetenciasDisponiveis(contract.competenciasDisponiveis)
        setSelectedContrato(event.target.value)
        setLoading('idle')

        if (contract.cdContrato == null) {
          throw new Error(
            'Não foi possivel encontrar um contrato com esse codigo.'
          )
        }
      })
      .catch((errors) => {
        reset()
        setAttachDocsList([])
        setNmFornecedor('')
        setCnpjFornecedor('')
        setContratoId(0)
        setfornecedorId(0)
        setSelectedContrato('')
        setLoading('idle')
        setContrato('')

        showModal({
          hideButtonCancel: true,
          message:
            errors?.message || 'Não foi possível obter os dados do contrato.',
          txtButtonConfirm: 'Fechar',
          type: errors?.message ? 'alert' : 'error'
        })
      })
  }

  const handleSearchAttachDocs = (idContract: number) => {
    setLoading('loading')
    ConventionServices()
      .getAnexados(idContract)
      .then(({ data }) => {
        if (!data.anexosPendentes && !data.anexos.length) {
          setLoading('idle')
          showModal({
            handleConfirm: () => {
              handleClearForm()
            },
            hideButtonCancel: true,
            message:
              'Contrato não possui documentação do tipo Acordo/Convenção.',
            txtButtonConfirm: 'Voltar',
            type: 'alert'
          })
          return
        }

        setAttachDocsList(
          data.anexos.concat(
            data.anexosPendentes !== null ? data.anexosPendentes : []
          )
        )
        setLoading('idle')
      })
      .catch(() => {
        // setMsgErro('Erro ao carregar lista de arquivos de Acordos e Convenções')
        setLoading('error')
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao carregar lista de arquivos de Acordos e Convenções',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  const handleViewDocument = (externalID: number) => {
    viewFile({
      gedExternalID: externalID
    })
  }

  const handleSelectedContract = (selected, contract) => {
    setNmFornecedor(contract.nmFornecedor)
    setCnpjFornecedor(contract.cnpjFornecedor)
    setfornecedorId(contract.idFornecedor)
    setContratoId(contract.idContrato)
    setContrato(contract.cdContrato)
    setSelectedContrato(selected)
  }

  useEffect(() => {
    if (contratoId) {
      handleSearchAttachDocs(contratoId)
    }
  }, [contratoId])

  const handleClearForm = () => {
    reset()
    setAttachDocsList([])
    setNmFornecedor('')
    setCnpjFornecedor('')
    setContratoId(0)
    setfornecedorId(0)
    setContrato('')
    setSelectedContrato('')
  }

  const getSindicatos = () => {
    setLoading('loading')

    SyndicateServices()
      .getFiltro({})
      .then((response) => {
        if (response.status === 200) {
          setSindicatos(response.data)
        }

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')

        showModal({
          hideButtonCancel: true,
          message: 'Ocorreu um erro ao obter os sindicatos.',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  interface IHandleSelectSindicado extends IGetFiltroResponse {
    formPosition: number
  }

  const handleSelectSindicato = (props: IHandleSelectSindicado) => {
    setValue(`sindicatos.${props.formPosition}.nmSindicato`, props.nmSindicato)
    setValue(`sindicatos.${props.formPosition}.cdCnpj`, props.cdCnpj)
    setValue(`sindicatos.${props.formPosition}.idSindicato`, props.idSindicato)
  }

  useEffect(() => {
    getSindicatos()
  }, [])

  const formIsValid = (): boolean => {
    const isValid = true
    const isNotValid = false

    if (nmFornecedor === '') {
      showModal({
        hideButtonCancel: true,
        message: 'Selecione um contrato válido',
        txtButtonConfirm: 'OK',
        type: 'error'
      })

      return isNotValid
    }

    if (
      watchSindicatos.filter((sindicato) => Boolean(sindicato.idSindicato))
        .length === 0
    ) {
      showModal({
        hideButtonCancel: true,
        message: 'Selecione um Sindicato válido',
        txtButtonConfirm: 'OK',
        type: 'error'
      })

      return isNotValid
    }

    return isValid
  }

  const handleChangeCompetencia = (event) => {
    setCompetencias(event.target.value)
    setValue('nuAnoMesCompetencia', event.target.value)
  }

  const handleSubmitForm = (data) => {
    if (data.nuAnoMesCompetencia.length == 0) {
      showModal({
        hideButtonCancel: true,
        message: 'Informe ao menos uma competência',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return
    }

    setLoading('loading')

    const obj = {
      cdHomologacao: data.cdHomologacao.trim(),
      contratoId: data.contratoId,
      dtVigenciaFim: moment(data.dtVigenciaFim).toISOString(),
      dtVigenciaInicio: moment(data.dtVigenciaInicio).toISOString(),
      flBancoHoras: eval(data.flBancoHoras),
      flHorasExtras: eval(data.flHorasExtras),
      flTurno: eval(data.flTurno),
      flValeAlimentacao: eval(data.flValeAlimentacao),
      flValeRefeicao: eval(data.flValeRefeicao),
      flVencido: data.flVencido,
      fornecedorId: data.fornecedorId,
      idsSindicato: [
        ...new Set(
          data.sindicatos
            .filter((sindicato) => Boolean(sindicato.idSindicato))
            .map((sindicato) => sindicato.idSindicato)
        )
      ],
      nuAnoMesCompetencia: data.nuAnoMesCompetencia,
      peInsalubridade: data.peInsalubridade,
      pePericulosidade: data.pePericulosidade,
      pePisoSalarial: data.pePisoSalarial,
      txObservacaoBancoHoras: data.txObservacaoBancoHoras,
      txObservacaoGeral: data.txObservacaoGeral,
      txObservacaoHorasExtras: data.txObservacaoHorasExtras,
      txObservacaoTurno: data.txObservacaoTurno,
      txTipo: data.txTipo,
      vlReajuste: data.vlReajuste
    }

    ConventionServices()
      .create(obj)
      .then((response) => {
        if (response.status === 200) {
          const { sucesso, mensagem, detalhe }: ResponseGeneric = response.data

          const saved = mensagem
            ?.split(',')
            .map((item: string) => FORMAT.competencia(item.trim()))
          const fail = detalhe
            ?.split(',')
            .map((item: string) => FORMAT.competencia(item.trim()))

          let msg = ''
          if (sucesso) {
            msg = 'Incluído com sucesso!'
            if (saved.length > 1)
              msg = `${msg} Competências: ${saved.join(', ')}`
            if (fail)
              msg = `${msg} | Não incluído por haver cadastro: ${fail.join(
                ', '
              )}`
          }

          showModal({
            hideButtonCancel: true,
            message: sucesso ? msg : 'Não foi possível salvar os dados!',
            txtButtonConfirm: 'OK',
            type: sucesso ? 'success' : 'error'
          })
        }
        setLoading('idle')
        handleClearForm()
        setSelectedTab(0)
      })
      .catch((err) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível salvar os dados!',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  return (
    <Form
      onSubmit={handleSubmit((data) => {
        if (!formIsValid()) {
          return
        }

        handleSubmitForm({
          ...data,
          contratoId: contratoId,
          flVencido: dtVigenciaFim < DATE_NOW,
          fornecedorId: fornecedorId
        })
      })}
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      marginBottom={SPACING.spacing100}
      padding={SPACING.spacing20}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <Grid
        gridCols="1fr 1fr 4fr 2fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        {selectedContrato === '' ? (
          <Col>
            <Span text="Contrato" />
            <Input
              placeholder="Nº"
              marginTop="0.2rem"
              name="cdContrato"
              value={cdContrato}
              onChange={(e) => setContrato(e.target.value)}
              inputIcon={<MagnifyingGlassIcon />}
              onClickIcon={(e) => handleSearchContrato(e)}
            />
          </Col>
        ) : (
          <Tooltip title="Tecle LIMPAR para selecionar outro contrato">
            <Col>
              <Span text="Contrato" />
              <Input
                placeholder="Nº"
                marginTop="0.2rem"
                name="cdContrato"
                value={cdContrato}
                onChange={(e) => setContrato(e.target.value)}
                inputIcon={<MagnifyingGlassIcon />}
                onClickIcon={(e) => handleSearchContrato(e)}
              />
            </Col>
          </Tooltip>
        )}
        <Input
          label="CNPJ/CPF/Cód. Fornecedor"
          placeholder="Nº"
          value={cnpjFornecedor}
          mask={
            isNaN(parseInt(cnpjFornecedor))
              ? ['NNNNNNNNNN']
              : ['###.###.###-##', '##.###.###/####-##']
          }
          required
          disabled
        />
        <Col>
          <Span text="Fornecedor" />
          <AutoCompleteInput
            marginTop="0.2rem"
            name="nmFornecedor"
            onChangeText={handleSearchContrato}
            value={nmFornecedor}
            options={contratos || []}
            optionValue="label"
            // label="Contrato"
            placeholder="Nome do Fornecedor"
            onChange={(selectedValue, value) => {
              handleSelectedContract(selectedValue, value)
            }}
            valueName="value"
            formValue="nmFornecedor"
            required
            disabled
          />
        </Col>
      </Grid>

      <Grid
        gridCols="1fr 1fr 4fr 2fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <SelectForm
          marginTop="0.1rem"
          control={control}
          errors={errors}
          formValue="txTipo"
          defaultValue=" "
          label="Tipo"
          listOptions={[
            { label: 'Acordo', value: 'Acordo' },
            { label: 'Convenção', value: 'Convencao' }
          ]}
          labelMenu="label"
          valueMenu="value"
          placeholder="Tipo"
          required
        />
        <Input
          marginTop="0.1rem"
          control={control}
          errors={errors}
          label="Registro de Homologação"
          placeholder="Nº do Registro"
          formValue="cdHomologacao"
          inputIcon={<MagnifyingGlassIcon />}
          onClickIcon={() => handleSearchCdHomologacao()}
          required
        />
        <Col marginTop="0.2rem">
          <Span text="Competências" />
          <Select
            labelId="multiple-chip-label"
            id="multiple-chip"
            multiple
            margin="dense"
            value={competencias}
            onChange={handleChangeCompetencia}
            variant="standard"
            renderValue={(selected) => (
              <Row gap="5px">
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={FORMAT.competencia(value)}
                    style={{ height: '26px' }}
                  />
                ))}
              </Row>
            )}
          >
            {competenciasDisponiveis?.map((competencia) => (
              <MenuItem key={competencia} value={competencia}>
                {FORMAT.competencia(competencia)}
              </MenuItem>
            ))}
          </Select>
        </Col>
      </Grid>
      <>
        {fields.map((field, index) => (
          <Grid
            key={index}
            gridCols="3fr 4fr 0.2fr 0.2fr 1.9fr"
            gridGap={`0 ${SPACING.spacing15}`}
            margin={`0 0 ${SPACING.spacing30} 0`}
          >
            <Col>
              <AutoCompleteSearchTerm
                control={control}
                errors={errors}
                onSelect={(selectedValue: IGetFiltroResponse) =>
                  handleSelectSindicato({
                    ...selectedValue,
                    formPosition: index
                  })
                }
                formValue={`sindicatos.${index}.cdCnpj`}
                options={
                  sindicatos.map((sindicato) => ({
                    ...sindicato,
                    cdCnpj: ApplyMasker(sindicato.cdCnpj, [
                      '##.###.###/####-##'
                    ])
                  })) || []
                }
                optionLabel="cdCnpj"
                optionValue="cdCnpj"
                label="CNPJ Sindicato"
              />
            </Col>

            <Col>
              <AutoCompleteSearchTerm
                control={control}
                errors={errors}
                onSelect={(selectedValue: IGetFiltroResponse) =>
                  handleSelectSindicato({
                    ...selectedValue,
                    formPosition: index
                  })
                }
                formValue={`sindicatos.${index}.nmSindicato`}
                options={
                  sindicatos.map((sindicato) => ({
                    ...sindicato,
                    cdCnpj: ApplyMasker(sindicato.cdCnpj, [
                      '##.###.###/####-##'
                    ])
                  })) || []
                }
                optionLabel="nmSindicato"
                optionValue="nmSindicato"
                label="Sindicato / Confederação"
              />
            </Col>
            <input
              style={{ display: 'none' }}
              {...register(`sindicatos.${index}.idSindicato`)}
            />

            <Button
              text="+"
              onClick={() =>
                append({ cdCnpj: '', idSindicato: 0, nmSindicato: '' })
              }
            />
            <If condition={index > 0}>
              <Button text="-" onClick={() => remove(index)} />
            </If>
          </Grid>
        ))}
      </>
      <Grid
        gridCols="1fr 1fr 1fr 1fr 1fr 1fr 2.1fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <CurrencyInput
          control={control}
          errors={errors}
          style="percent"
          label="Periculosidade %"
          placeholder="%"
          maxLength={5}
          formValue="pePericulosidade"
          required
        />
        <CurrencyInput
          control={control}
          errors={errors}
          style="percent"
          label="Insalubridade %"
          placeholder="%"
          maxLength={5}
          formValue="peInsalubridade"
          required
        />
        <CurrencyInput
          control={control}
          errors={errors}
          label="Piso Salarial"
          placeholder="%"
          formValue="pePisoSalarial"
          required
        />
        <CurrencyInput
          control={control}
          errors={errors}
          style="percent"
          label="Reajuste %"
          placeholder="%"
          maxLength={5}
          formValue="vlReajuste"
          required
        />
        <SelectForm
          control={control}
          errors={errors}
          formValue="flValeAlimentacao"
          defaultValue=" "
          label="Vale Alimentação"
          listOptions={[
            { label: 'Sim', value: 'true' },
            { label: 'Não', value: 'false' }
          ]}
          labelMenu="label"
          valueMenu="value"
          placeholder="Vale Alimentação"
          required
        />
        <SelectForm
          control={control}
          errors={errors}
          formValue="flValeRefeicao"
          defaultValue=" "
          label="Vale Refeição"
          listOptions={[
            { label: 'Sim', value: 'true' },
            { label: 'Não', value: 'false' }
          ]}
          labelMenu="label"
          valueMenu="value"
          placeholder="Vale Refeição"
          required
        />
      </Grid>
      <Grid
        gridCols="1fr 2fr 1fr 2fr 2.05fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <SelectForm
          control={control}
          errors={errors}
          formValue="flHorasExtras"
          defaultValue=" "
          label="Horas Extras"
          listOptions={[
            { label: 'Sim', value: 'true' },
            { label: 'Não', value: 'false' }
          ]}
          labelMenu="label"
          valueMenu="value"
          placeholder="Horas Extras"
          required
        />
        <Input
          control={control}
          errors={errors}
          label="Obs. Horas Extras"
          placeholder="Observação"
          formValue="txObservacaoHorasExtras"
        />
        <SelectForm
          control={control}
          errors={errors}
          formValue="flBancoHoras"
          defaultValue=" "
          label="Banco de Horas"
          listOptions={[
            { label: 'Sim', value: 'true' },
            { label: 'Não', value: 'false' }
          ]}
          labelMenu="label"
          valueMenu="value"
          placeholder="Banco de Horas"
          required
        />
        <Input
          control={control}
          errors={errors}
          label="Obs. Banco de Horas"
          placeholder="Observação"
          formValue="txObservacaoBancoHoras"
        />
      </Grid>
      <Grid
        gridCols="1fr 2fr 3fr 2fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <SelectForm
          control={control}
          errors={errors}
          formValue="flTurno"
          defaultValue=" "
          label="Turno"
          listOptions={[
            { label: 'Sim', value: 'true' },
            { label: 'Não', value: 'false' }
          ]}
          labelMenu="label"
          valueMenu="value"
          placeholder="Turno"
          required
        />
        <Input
          control={control}
          errors={errors}
          label="Obs. Turno"
          placeholder="Observação"
          formValue="txObservacaoTurno"
        />
        <Input
          control={control}
          errors={errors}
          label="Obs. Gerais"
          placeholder="Observação"
          formValue="txObservacaoGeral"
        />
      </Grid>
      <Grid
        gridCols="1fr 1fr 1fr 5fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <Picker
          defaultValue={DATE_NOW}
          label="Início Vigência"
          value={dtVigenciaInicio || DATE_NOW}
          formValue="dtVigenciaInicio"
          maxDate={moment(dtVigenciaFim) || null}
          control={control}
          errors={errors}
        />
        <Picker
          defaultValue={DATE_NOW}
          label="Fim Vigência"
          formValue="dtVigenciaFim"
          value={dtVigenciaFim || DATE_NOW}
          minDate={moment(dtVigenciaInicio) || null}
          control={control}
          errors={errors}
        />
        <Col marginLeft={SPACING.spacing15}>
          <Span text="Vencido" />
          {dtVigenciaFim < DATE_NOW ? (
            <Row marginTop={SPACING.spacing10}>
              <CheckBoxIcon color="primary" />
              <Span
                marginTop={SPACING.spacing5}
                marginLeft={SPACING.spacing5}
                marginRight={SPACING.spacing5}
                text="Sim"
              />
              <CheckBoxOutlineBlankIcon color={'disabled'} />
              <Span
                marginTop={SPACING.spacing5}
                marginLeft={SPACING.spacing5}
                marginRight={SPACING.spacing5}
                text="Não"
              />
            </Row>
          ) : (
            <Row marginTop={SPACING.spacing10}>
              <CheckBoxOutlineBlankIcon color={'disabled'} />
              <Span
                marginTop={SPACING.spacing5}
                marginLeft={SPACING.spacing5}
                marginRight={SPACING.spacing5}
                text="Sim"
              />
              <CheckBoxIcon color="primary" />
              <Span
                marginTop={SPACING.spacing5}
                marginLeft={SPACING.spacing5}
                marginRight={SPACING.spacing5}
                text="Não"
              />
            </Row>
          )}
        </Col>
      </Grid>
      <ConsultDocsTable
        attachDocsList={attachDocsList}
        handleViewDocument={handleViewDocument}
      />
      <Row justifyContent="flex-end">
        <Button
          text="Limpar"
          variantButton="text"
          onClick={() => {
            reset()
            handleClearForm()
          }}
        />
        <Button
          text="SALVAR"
          disabled={!areDatesValid()}
          type="submit"
          marginLeft={SPACING.spacing20}
        />
      </Row>
    </Form>
  )
}
